import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { IconAcomba } from '../../../assets/icons/IconAcomba';
import { useAcombaBridgeStore } from '../../../stores/acombaBridgeStore';
import { useAppNavigate } from '../../../hooks/useAppNavigate';
import { TranslationKey } from '../../../i18n/translationKeys';

export const AcombaBridgeIcon: React.FC = () => {
    const { t } = useTranslation();
    const appNavigate = useAppNavigate();
    const { pathname } = useLocation();
    const bridgeStatus = useAcombaBridgeStore((s) => s.bridgeStatus);
    const sessionStatus = useAcombaBridgeStore((s) => s.sessionStatus);
    const sessionReason = useAcombaBridgeStore((s) => s.sessionReason);

    const isOnAcombaPage = pathname === '/acomba';

    // Bridge is up but the Acomba SDK on this machine is the problem — either not reachable at all
    // (SdkUnavailable) or too old for the company file (SdkVersionMismatch). Both are actionable,
    // machine-level cases that must not read as the generic "not connected"; they share one distinct
    // badge and differ only by label.
    const sdkProblem =
        bridgeStatus === 'reachable' &&
        sessionStatus !== 'connected' &&
        (sessionReason === 'SdkUnavailable' || sessionReason === 'SdkVersionMismatch')
            ? sessionReason
            : null;

    const badgeClass = (() => {
        if (bridgeStatus === 'checking') return 'bg-indicator-neutral animate-pulse';
        if (bridgeStatus === 'unreachable') return 'bg-indicator-danger';
        if (sessionStatus === 'connected') return 'bg-indicator-success';
        if (sdkProblem) return 'bg-indicator-accent';
        return 'bg-indicator-warning';
    })();

    const statusLabel = (() => {
        if (bridgeStatus === 'checking') return t(TranslationKey.AcombaBridge.Status.Checking);
        if (bridgeStatus === 'unreachable') return t(TranslationKey.AcombaBridge.Status.NoBridge);
        if (sessionStatus === 'connected') return t(TranslationKey.AcombaBridge.Status.Connected);
        if (sdkProblem === 'SdkVersionMismatch') return t(TranslationKey.AcombaBridge.Status.SdkVersionMismatch);
        if (sdkProblem === 'SdkUnavailable') return t(TranslationKey.AcombaBridge.Status.SdkUnavailable);
        return t(TranslationKey.AcombaBridge.Status.NotConnected);
    })();

    return (
        <button
            type="button"
            data-testid="acomba-bridge-icon-btn"
            aria-label={t(TranslationKey.AcombaBridge.IconAriaLabel)}
            title={statusLabel}
            disabled={isOnAcombaPage}
            onClick={() => appNavigate('/acomba')}
            className="relative p-xs rounded hover:bg-primary-200 hover:cursor-pointer transition-colors disabled:cursor-default disabled:hover:bg-transparent"
        >
            <IconAcomba />
            <span
                data-testid="acomba-bridge-status-badge"
                className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${badgeClass}`}
            >
                <span className="sr-only">{statusLabel}</span>
            </span>
        </button>
    );
};
